import React, { useEffect, useRef } from 'react';

import _ from 'lodash';
import queryString from 'query-string';
import {
  TableContainer,
  TableCell,
  Table,
  TableBody,
  TableRow,
  TableHead,
  TablePagination,
  Box,
  Typography,
  Checkbox,
  Paper,
  colors
} from '@mui/material';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import Skeleton from '@mui/material/Skeleton';

import { useTable, usePagination, Column, useFilters, useRowSelect, useSortBy, SortingRule } from 'react-table';

import TextFilter from './filters/text';
import usePrevious from 'src/hooks/Commons/usePrevious';
import { generatePaginationURLWithPageV2, generateURLWithFilter, generateURLWithPerPage } from 'src/utils/commons/url';
import PaginationActions from './PaginationActions';
import { useLocation, useNavigate } from 'react-router';
import { NoFoodTwoTone } from '@mui/icons-material';

type IndexTableProps<T extends object> = {
  data: T[];
  columns: Column<T>[];
  loading: boolean;
  searchText: string;
  pageCount: number;
  totalDataCount: number;

  onFetchData?: (params: any) => void;
  [key: string]: any;
  displayPaging?: boolean;
};

function IndexTable<T extends object>({
  displayPaging = true,
  columns,
  data,
  loading,
  searchText,
  pageCount: controlledPageCount,
  totalDataCount,
  setFilters,
  headerRef,
  onFetchData,
  withSelection
}: IndexTableProps<T>) {
  const location = useLocation();
  const history = useNavigate();
  const urlQuery = queryString.parse(location?.search);
  const { page: initPage = '1', take: initSize = '10' } = urlQuery;

  const defaultColumn = React.useMemo(
    () => ({
      Filter: TextFilter
    }),
    []
  );

  const tableHook = useTable<T>(
    {
      columns,
      data,
      defaultColumn,
      pageCount: controlledPageCount + 1,
      initialState: {
        pageIndex: Number(initPage) - 1,
        pageSize: Number(initSize),
        hiddenColumns: columns.map((column: any) => (column.hide ? column.accessor || column.id : null))
      },
      manualPagination: true,
      manualSortBy: true,
      manualFilters: true,
      autoResetFilters: false,
      autoResetPage: false
    },
    useFilters,
    useSortBy,
    usePagination,
    useRowSelect,
    hooks => {
      if (withSelection) {
        hooks.allColumns.push(_columns => [
          {
            id: 'selection',
            sortable: false,
            width: 50,
            Cell: ({ row }: any) => (
              <div className="w-24">
                <Checkbox
                  checkedIcon={<CheckBoxOutlinedIcon />}
                  icon={<CheckBoxOutlineBlankOutlinedIcon />}
                  {...row.getToggleRowSelectedProps()}
                  onClick={(event: React.MouseEvent) => event.stopPropagation()}
                />
              </div>
            )
          },
          ..._columns
        ]);
      }
    }
  );
  const { headerGroups, page, state, getTableProps, prepareRow, gotoPage, setPageSize } = tableHook;
  const { pageIndex, pageSize, sortBy } = state;
  const prevSortBy = usePrevious(sortBy);

  useEffect(() => {
    setFilters(state.filters);
  }, [state, setFilters]);

  // Show page & per_page on browser url by default
  useEffect(() => {
    const { page: newPage, take: newSize } = urlQuery;
    if (!newPage || !newSize) {
      const search = generateURLWithPerPage(location.search, pageSize);
      history('?' + search);
    }
  }, [history, location.search, urlQuery, pageSize]);

  /**
   * generateSortRuleStrings
   * @param {SortingRules<string>} sortRules
   * @return {string[]}
   */
  const generateSortRuleStrings = (sortRules: SortingRule<string>[]): string[] => {
    return sortRules.map(sort => {
      const sortField = _.snakeCase(sort.id);
      return `${sortField} ${sort.desc ? 'desc' : 'asc'}`;
    });
  };

  /**
   * Effect for a table changes: pageSize, pageIndex, sort
   * Searching isn't a table event, so we don't handle here
   * Sort:
   *  - Now we just support single sort field, so we just get the first element of sortRuleStrings
   *  - There are 3 states: asc, desc, and reset (clear sort)
   */
  useEffect(() => {
    if (onFetchData) {
      const sortRuleStrings = generateSortRuleStrings(sortBy);
      const fetchParams = {
        ...urlQuery,
        take: pageSize,
        page: pageIndex + 1,
        'q[s]': sortRuleStrings[0]
      };
      onFetchData(fetchParams);
    }
    // eslint-disable-next-line
  }, [onFetchData, pageSize, pageIndex, sortBy]);

  /**
   * TODO: create useDidMount hook
   */

  const didMountRef = useRef(false);

  /**
   * When doing search, we always go to the first page
   * to prevent unexpect behavior of pagination.
   * This behavior just applies after table mounted, not on the first time
   * Using useRef hook to avoid the extra rerender
   */
  useEffect(() => {
    if (didMountRef.current) {
      gotoPage(0);
    } else {
      didMountRef.current = true;
      if (headerRef?.current) headerRef.current = tableHook;
    }
    // eslint-disable-next-line
  }, [searchText]);

  // did mount
  // fetch data first time
  useEffect(() => {
    handleFetchData({
      pIndex: pageIndex + 1,
      pSize: pageSize,
      sBy: sortBy,
      query: urlQuery
    });
    // eslint-disable-next-line
  }, []);

  // effect for filter
  // useEffect(() => {
  //   if (prevFilters && !_.isEqual(filters, prevFilters)) {
  //     const queryObject = generateQueryObject(filters);

  //     // reset page to first page
  //     queryObject.page = '1';
  //     gotoPage(0);

  //     // update browser url
  //     const search = generateURLWithFilter(location.search, queryObject);
  //     history('?' + search);

  //     // fetch data
  //     handleFetchData({
  //       pIndex: 1,
  //       pSize: pageSize,
  //       sBy: sortBy,
  //       query: { ...urlQuery, ...queryObject }
  //     });
  //   }
  // });

  // effect for sortby
  useEffect(() => {
    if (_.size(sortBy) && !_.isEqual(sortBy, prevSortBy)) {
      const queryObject = { ...urlQuery };

      // reset page to first page
      queryObject.page = '1';
      gotoPage(0);

      // update browser url
      const search = generateURLWithFilter(location.search, queryObject);
      history('?' + search);

      // fetch data
      handleFetchData({
        pIndex: 1,
        pSize: pageSize,
        sBy: sortBy,
        query: { ...queryObject }
      });
    }
  });

  const handleFetchData = ({
    pIndex,
    pSize,
    sBy,
    query
  }: {
    pIndex: number;
    pSize: number;
    sBy: SortingRule<string>[];
    query: any;
  }) => {
    if (onFetchData) {
      const sortRuleStrings = generateSortRuleStrings(sBy);
      const fetchParams = {
        ...query,
        take: pSize,
        page: pIndex,
        'q[s]': sortRuleStrings[0]
      };
      onFetchData(fetchParams);
    }
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null, newPage: number) => {
    gotoPage(newPage);
    const search = generatePaginationURLWithPageV2(location.search, newPage + 1);
    history('?' + search);

    handleFetchData({
      pIndex: newPage + 1,
      pSize: pageSize,
      sBy: sortBy,
      query: urlQuery
    });
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    /**
     * After change page size, we will go to the first page
     * to prevent unhandled situations
     */
    gotoPage(0);
    const search = generateURLWithPerPage(location.search, newSize);
    history('?' + search);
    handleFetchData({
      pIndex: 1,
      pSize: newSize,
      sBy: sortBy,
      query: urlQuery
    });
  };

  // Render the UI for your table
  return (
    <>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {/* <TableContainer sx={{ maxHeight: 440 }}> */}
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table
            {...getTableProps()}
            size="small"
            stickyHeader={true}
            className="h-0"
            aria-label="a dense sticky table"
          >
            <TableHead>
              {headerGroups.map((headerGroup: any, index: number) => (
                <TableRow
                  {...headerGroup.getHeaderGroupProps()}
                  key={index}
                  style={{ padding: 0 }}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  {headerGroup.headers.map((column: any, index: number) => (
                    <TableCell
                      className={`whitespace-no-wrap align-top pb-4 px-8 overflow-hidden first:pl-4 last:pr-8 capitalize ${
                        column.displayHighlight && 'bg-blue-200'
                      }`}
                      {...column.getHeaderProps()}
                      key={index}
                      style={{ width: column.width, minWidth: column.minWidth }}
                    >
                      {/* {column.render('Header')} */}
                      {/* <Typography component="div" className="text-13 font-400 text-secondaryLight pl-8 w-full">
                      {column.render('Header')}
                    </Typography> */}

                      <div {...column.getHeaderProps()} className="flex justify-start items-center">
                        <Typography component="div" className="text-sm font-bold w-full">
                          {column.render('Header')}
                        </Typography>
                      </div>
                      {column.displayFilter && <div className="mt-4">{column.render('Filter')}</div>}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>

            {loading ? (
              <TableBody>
                <TableRow className="transition-colors duration-300 hover:bg-blue-gray-100 hover:bg-opacity-50 h-64 border-b last:border-b-0">
                  {columns?.map((value, index) => {
                    return (
                      // tslint:disable-next-line: jsx-key
                      <TableCell key={index} className={'py-6 px-16 first:pl-40 last:pr-40'}>
                        <Skeleton variant="text" animation="wave" />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableBody>
            ) : (
              <>
                {totalDataCount == 0 && (
                  <TableBody>
                    <TableRow>
                      <TableCell colSpan={999}>
                        <Typography p={2} textAlign="center">
                          Không có dữ liệu
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
                <TableBody>
                  {page.map((row, i) => {
                    prepareRow(row);
                    return (
                      <TableRow hover {...row.getRowProps()} key={i}>
                        {row.cells.map((cell: any, index: number) => {
                          return (
                            <TableCell
                              {...cell.getCellProps()}
                              key={index}
                              className={cell.column.displayHighlight && `bg-blue-200`}
                            >
                              {cell.render('Cell')}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </>
            )}
          </Table>
        </TableContainer>
      </Paper>
      {displayPaging && data.length > 0 && (
        <Box p={2}>
          <TablePagination
            component="div"
            classes={{
              root: 'overflow-hidden flex-shrink-0 border-0 border-t-2 mt-auto',
              spacer: 'w-0 max-w-0',
              toolbar: 'flex justify-end'
            }}
            rowsPerPageOptions={[10, 25]}
            colSpan={5}
            count={totalDataCount}
            rowsPerPage={pageSize}
            page={pageIndex}
            SelectProps={{
              inputProps: { 'aria-label': 'rows per page' },
              native: false
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={PaginationActions}
          />
        </Box>
      )}
    </>
  );
}

export default IndexTable;
